/**
 * The Medium tier's pixel-art surfaces (lifted from the dungeon-floors
 * prototype's `dungeon-surfaces.js`): world-locked texels from the shared
 * tone atlas (`pixel-atlas.ts`), quantized to the theme's ramps with an
 * ordered dither and a low-frequency level shift. One program per surface
 * kind (compile-time constants, `shaders/surfaces.ts`). Albedo is the ramp
 * colour times (instance tint / palette reference), so an instance tinted with
 * its palette colour shows the ramp exactly. The uniforms belong to the view
 * and are refilled for each floor (theme and seed), so floors reuse the
 * compiled programs.
 */
import type { Theme } from '@ab/content-schema';
import * as THREE from 'three';
import { presentation } from './constants.ts';
import type { ShaderHook } from './material-hooks.ts';
import { pixelAtlas } from './pixel-atlas.ts';
import { type SurfaceKind, surfaceFragment, surfaceFragmentHeader, surfaceVertex, surfaceVertexHeader } from './shaders/surfaces.ts';

export type { SurfaceKind } from './shaders/surfaces.ts';

type Ramps = Theme['tileMaterials']['ramps'];
type RampSlot = readonly [keyof Ramps, 'floor' | 'wall' | 'timber' | 'iron'];

/** Ramp slots per kind with the palette colour each is referenced to. */
const slots = (kind: SurfaceKind): readonly RampSlot[] => {
  switch (kind) {
    case 'stone':
    case 'rockmass':
    case 'boulder':
      return [
        ['ground', 'floor'],
        ['rock', 'wall'],
        ['cap', 'wall'],
      ];
    case 'timber':
      return [['timber', 'timber']];
    case 'iron':
      return [['iron', 'iron']];
  }
};

/** A hue at unchanged luminance (a speck or a warm patch), kept toward white by `keep`. */
const hueOf = (hex: string, keep: number): THREE.Vector3 => {
  const c = new THREE.Color(hex);
  const l = Math.max(1e-4, 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b);
  return new THREE.Vector3(c.r / l, c.g / l, c.b / l).lerp(new THREE.Vector3(1, 1, 1), keep);
};

export type SurfaceProgram = { readonly hook: ShaderHook; update(theme: Theme, seed: number): void };

/** The hook of one surface kind and the uniforms it reads, filled per floor by `update`. */
export const surfaceProgram = (kind: SurfaceKind): SurfaceProgram => {
  const k = presentation.medium.surfaces;
  const u = {
    dqSurface: { value: new THREE.Vector4() },
    dqPix: { value: new THREE.Vector4() },
    dqSpeck: { value: new THREE.Vector4() },
    dqCapCool: { value: new THREE.Vector3() },
    dqSpeckWarm: { value: new THREE.Vector3() },
    dqSpeckCool: { value: new THREE.Vector3() },
    dqFogContrast: { value: 1 },
    dqGroundWarm: { value: new THREE.Vector4() },
    dqAtlas: { value: null as THREE.Texture | null },
    dqRamp: { value: new Float32Array(72) },
    dqRef: { value: new Float32Array(9).fill(1) },
    dqRampN: { value: new THREE.Vector3(2, 2, 2) },
  };
  const size = presentation.medium.atlas.size;
  const hook: ShaderHook = {
    key: `surface-${kind}`,
    uniforms: u,
    vertexHeader: surfaceVertexHeader,
    fragmentHeader: surfaceFragmentHeader(kind, size),
    vertex: [{ chunk: 'worldpos_vertex', after: surfaceVertex }],
    fragment: [{ chunk: 'color_fragment', after: surfaceFragment(kind, size) }],
  };
  const c = new THREE.Color();
  return {
    hook,
    update(theme, seed) {
      const m = theme.tileMaterials;
      u.dqRamp.value.fill(0);
      slots(kind).forEach(([ramp, reference], slot) => {
        const colors = m.ramps[ramp];
        colors.forEach((hex, i) => {
          c.set(hex);
          u.dqRamp.value.set([c.r, c.g, c.b], (slot * 8 + i) * 3);
        });
        u.dqRampN.value.setComponent(slot, colors.length);
        c.set(theme.palette[reference]);
        u.dqRef.value.set([Math.max(c.r, k.minReference), Math.max(c.g, k.minReference), Math.max(c.b, k.minReference)], slot * 3);
      });
      u.dqSurface.value.set(m.detailScalePercent / 100, m.strataScalePercent / 100, m.surfaceGrainPercent / 100, (seed >>> 0) % k.seedPeriod);
      const last = kind === 'boulder' ? 1 : kind === 'iron' ? m.railTopTonePercent / 100 : 0;
      u.dqPix.value.set(m.texelDensity, m.ditherStrengthPercent / 100, m.macroStrengthPercent / 100, last);
      u.dqSpeck.value.set(m.speckDensityPercent / 100, m.pebbleVariationPercent / 100, m.capTexel, m.capCoolSharePercent / 100);
      u.dqCapCool.value.copy(hueOf(m.capCool, 0));
      u.dqSpeckWarm.value.copy(hueOf(m.speckWarm, k.speckKeep));
      u.dqSpeckCool.value.copy(hueOf(m.speckCool, k.speckKeep));
      u.dqFogContrast.value = m.fogContrastPercent / 100;
      const warm = hueOf(m.groundWarm, k.groundWarmKeep);
      u.dqGroundWarm.value.set(warm.x, warm.y, warm.z, m.groundWarmSharePercent / 100);
      u.dqAtlas.value = pixelAtlas(m);
    },
  };
};

/**
 * `dqTop` per vertex: the height of the top edge of a vertex's face (the lit
 * rim on rock faces). Geometry without faces of its own carries a value far
 * above any face (the shader ignores it).
 */
export const addSurfaceTop = <G extends THREE.BufferGeometry>(geometry: G, value: number = presentation.medium.surfaces.noTop): G => {
  if (geometry.getAttribute('dqTop') === undefined) {
    geometry.setAttribute('dqTop', new THREE.Float32BufferAttribute(new Float32Array(geometry.getAttribute('position').count).fill(value), 1));
  }
  return geometry;
};
